import { useMemo } from 'react';
import { toParagraphs } from '../../../supabase/functions/_shared/aiReply';
import { useTypewriter } from '@/hooks/useTypewriter';

interface Segment { text: string; em?: boolean; speech?: boolean }

/** *action* / _action_ -> italic; "dialogue" -> slightly brighter. Markers removed. */
function segment(paragraph: string): Segment[] {
  const out: Segment[] = [];
  const re = /\*([^*\n]+)\*|_([^_\n]+)_|("[^"\n]+"|“[^”\n]+”)/g;
  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(paragraph))) {
    if (m.index > last) out.push({ text: paragraph.slice(last, m.index) });
    if (m[1] || m[2]) out.push({ text: m[1] ?? m[2], em: true });
    else out.push({ text: m[3], speech: true });
    last = m.index + m[0].length;
  }
  if (last < paragraph.length) out.push({ text: paragraph.slice(last) });
  return out;
}

interface FormattedReplyProps {
  text: string;
  /** Type the reply out (only the newest reply; history appears at once). */
  animate?: boolean;
  onTick?: () => void;
  onDone?: () => void;
  className?: string;
}

/**
 * A protagonist's reply as readable prose: cleaned of model residue, split
 * into paragraphs (shared rule: supabase/functions/_shared/aiReply.ts), and
 * typed out like the Neural Assistant. Tap to show the rest at once.
 */
export default function FormattedReply({ text, animate = false, onTick, onDone, className = '' }: FormattedReplyProps) {
  const paragraphs = useMemo(() => toParagraphs(text).map(segment), [text]);
  const total = useMemo(() => paragraphs.reduce((n, p) => n + p.reduce((m, s) => m + s.text.length, 0), 0), [paragraphs]);
  const { shown, typing, skip } = useTypewriter(total, animate, { onTick, onDone });

  let budget = shown;
  return (
    <div className={`space-y-2.5 ${className}`} onClick={typing ? skip : undefined} aria-live={animate ? 'polite' : undefined}>
      {paragraphs.map((segs, pi) => {
        if (budget <= 0 && typing) return null;
        const nodes = segs.map((s, si) => {
          if (budget <= 0) return null;
          const part = s.text.slice(0, budget);
          budget -= s.text.length;
          if (s.em) return <em key={si} className="text-slate-400">{part}</em>;
          if (s.speech) return <span key={si} className="text-slate-100">{part}</span>;
          return <span key={si}>{part}</span>;
        });
        const isLastVisible = typing && budget <= 0;
        return (
          <p key={pi} className="leading-relaxed">
            {nodes}
            {isLastVisible && <span className="inline-block w-1.5 h-3.5 ml-0.5 align-[-2px] bg-cyan-400/60 animate-pulse" aria-hidden />}
          </p>
        );
      })}
    </div>
  );
}
